import { useCallback, useState } from 'react';

export type DistributionViewMode = 'pie' | 'bars';

/** Distinct full keys (no string concat) so firmware vs location can never share storage by mistake */
export const DISTRIBUTION_VIEW_STORAGE_KEYS = {
  firmware: 'dms.dashboard.distributionView.v4.firmware',
  deviceLocation: 'dms.dashboard.distributionView.v4.deviceLocation',
} as const;

export type DistributionPersistenceScope = keyof typeof DISTRIBUTION_VIEW_STORAGE_KEYS;

function read(storageKey: string, defaultMode: DistributionViewMode): DistributionViewMode {
  try {
    const v = localStorage.getItem(storageKey);
    if (v === 'pie' || v === 'bars') {
      return v;
    }
  } catch {
    /* private mode or quota */
  }
  return defaultMode;
}

function write(storageKey: string, mode: DistributionViewMode) {
  try {
    localStorage.setItem(storageKey, mode);
  } catch {
    /* ignore */
  }
}

/**
 * Persists pie vs. inline bar list preference per card in localStorage.
 */
export function usePersistedDistributionView(
  scope: DistributionPersistenceScope,
  defaultMode: DistributionViewMode = 'pie'
): [DistributionViewMode, (mode: DistributionViewMode) => void] {
  const storageKey = DISTRIBUTION_VIEW_STORAGE_KEYS[scope];

  const [mode, setModeState] = useState<DistributionViewMode>(() =>
    read(storageKey, defaultMode)
  );

  const setMode = useCallback(
    (next: DistributionViewMode) => {
      setModeState(next);
      write(storageKey, next);
    },
    [storageKey]
  );

  return [mode, setMode];
}
